import _ from 'lodash'
import * as THREE from 'three'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import BaseTool from '@glass-designer/tools/baseTool'

import EditingCutout from '@glass-designer/objects/editingCutout'
import EditingItem from '@glass-designer/objects/editingPoint'
import DatumLink from '@glass-designer/model/datumLink'

import GlassDesignerTypes from '@glass-designer/types'
import EditingPoint from '@glass-designer/objects/editingPoint'


// Segment Add Points Tool
// Called when 'Add Points' selected when editing a segment
export default class SegmentAddPointsTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.segmentAddPoints
    this.selectedShape = null
    this.selectedSegmentIndex = 0
    this.pointCount = 1

    this.editingDatumLinkId = null
    this.editingDatumStoredDistances = []

    this.cutoutPosition = new THREE.Vector2()

    this.editingPoints = []
    this.datumLinks = []
  }


  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive()

    this.selectedShape = options.glassShape
    this.selectedSegmentIndex = options.glassSegmentIndex
    this.pointCount = 1
    
    this._updateEditingPoints(this.pointCount)

    this.displayToolSettingsPopover()

    this.editor.setDimensionsEnabled([GlassDesignerTypes.DimensionTypes.editingPointDimension])
    this.editor.setShapeSelectionEnabled(false)
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    // Clear all the editing points
    this._updateEditingPoints(0)
    this.editor.component.hidePopover()

    this.editor.resetDimensionsEnabled()
    this.editor.resetSelectionEnabled()
  }

  // Update tool options
  updatePointsEdit(options) {
    this.pointCount = options.pointCount
    this._updateEditingPoints(this.pointCount)
  }

  // Apply point slicing to the selected segment
  applyPointsEdit(options) {
    this.pointCount = options.pointCount
    this._updateEditingPoints(this.pointCount)

    this.editor.startUndoGroup('Adding shape points')
    this.editor.addUndoShape(this.selectedShape)

    this.editingPoints.reverse().forEach(editingPnt => {
      this.selectedShape.subdivideSegment(this.selectedSegmentIndex, editingPnt.position)
    })

    this.editor.closeCurrentUndoGroup()

    // Rebuild dimensions
    this.editor.resetDimensionsEnabled()
    this.editor.resetSelectionEnabled()
    this.editor.onShapeUpdated(this.selectedShape)

    this.closeTool()
  }


  displayToolSettingsPopover() {
    // Fill in options
    var popoverOptions = {
      pointCount: this.pointCount,
      context: this,
      updateCallback: (context, options) => {
        context.updatePointsEdit(options)
      },
      cancelCallback: (context, userCancelled) => {
        if(userCancelled)
          context.closeTool()
      },
      applyCallback: (context, options) => {
        context.applyPointsEdit(options)
      },
    }

    var displayOptions = {
      horizontalPosition: this._calculatePopoverHorizontalPosition()
    }

    this.editor.component.displayAddPointsToolPopover(popoverOptions, displayOptions)
  }


  /**
   * Update the editing points for the segment
   * Only updates if point count changed
   * If update required, subdivides the segment equally for each point
   */
  _updateEditingPoints(pointCount) {
    if(pointCount == this.editingPoints.length)
      return

    this.datumLinks = []

    // Add/Remove points
    let removeCount = Math.max(this.editingPoints.length - pointCount, 0)
    let addCount = Math.max(pointCount - this.editingPoints.length, 0)

    while(removeCount-- > 0) {
      let editingPnt = this.editingPoints.pop()
      editingPnt.dispose()
    }
    while(addCount-- > 0) {
      var editingPnt = new EditingPoint(this.editor)
      this.editingPoints.push(editingPnt)
    }


    // Update points
    // Note: Does not handle arcs
    if(this.editingPoints.length > 0) {
      let segment = this.selectedShape.getSegmentForIndex(this.selectedSegmentIndex)
      let fraction = 1.0 / (this.editingPoints.length+1)

      this.editingPoints.forEach((editingPoint, index) => {
        let segFraction = (index+1) * fraction
        
        editingPoint.setPosition(segment.getPoint(segFraction))

        var datumLink = new DatumLink()
        datumLink.update({
          sourceShapeId: this.selectedShape.id,
          segmentId: segment.id,
          sourceDatumType: GlassDesignerTypes.DatumTypes.position,
          sourcePosPoint: segment.getPoint(index * fraction),
          sourcePosDir: segment.getDirection(),
          sourceOffsetDirection: 1,
          sourceOffsetDistance: segment.getLength() * fraction,
          userData: { index: index }
        })

        this.datumLinks.push(datumLink)

        // Last datum link
        if(index == this.editingPoints.length-1) {
          datumLink = new DatumLink()
          datumLink.update({
            sourceShapeId: this.selectedShape.id,
            segmentId: segment.id,
            sourceDatumType: GlassDesignerTypes.DatumTypes.position,
            sourcePosPoint: editingPoint.position,
            sourcePosDir: segment.getDirection(),
            sourceOffsetDirection: 1,
            sourceOffsetDistance: segment.getLength() * fraction,
            userData: { index: index+1 }
          })
          this.datumLinks.push(datumLink)
        }
      })

      this.editingPoints.forEach((editingPoint, index) => {
        editingPoint.setDatumLinks(_.compact([index == 0 ? this.datumLinks[index] : null, this.datumLinks[index+1]]))
      })
    }

    this.editor.onToolEditingUpdated(this.selectedShape, this.editingPoints)
    this.editor.render()
  }


  // A dimension used while editing in this tool was clicked
  editingDimensionClicked(dimension) {
    if(dimension.dimType != GlassDesignerTypes.DimensionTypes.editingPointDimension)
      return

    let selectedDatumLink = this.datumLinks.find(datumLink => datumLink.id == dimension.linkData.datumLinkId)
    if(!selectedDatumLink)
      return
    
    // Fill in options
    var popoverOptions = {
      value: dimension.getTotalLength(),
      displayUserFixedField: true,
      allowPlusMinus: false,
      isUserFixed: selectedDatumLink.isFixedDatumLink,
      title: 'Length',
      addon: 'mm',
      context: this,
      updateCallback: (context, options) => {
        context.updateDimensionEdit(options)
      },
      cancelCallback: (context, userCancelled) => {
        context.cancelDimensionEdit(userCancelled)
      },
      applyCallback: (context, options) => {
        context.applyDimensionEdit(options)
      }
    }

    this.editor.component.displayNumberEntryPopover(popoverOptions)

    this.editingDatumLinkId = dimension.linkData.datumLinkId
    this.editingDatumStoredDistances = this.datumLinks.map(datum => datum.sourceOffsetDistance)
  }

  updateDimensionEdit(options) {
    this._updateEditingPointsFromLinearDimensionEdit(this.selectedShape, this.editingDatumLinkId, options.value, options.isUserFixed)
  }
  applyDimensionEdit(options) {
    this._updateEditingPointsFromLinearDimensionEdit(this.selectedShape, this.editingDatumLinkId, options.value, options.isUserFixed)

    this.displayToolSettingsPopover()
  }

  // Update the editing points from a linear dimension editing
  _updateEditingPointsFromLinearDimensionEdit(shape, datumLinkId, newLength, isUserFixed) {
    let datumLink = this.datumLinks.find(datum => datum.id == datumLinkId)
    if(!datumLink)
      return

    let segment = shape.getSegmentById(datumLink.segmentId)
    if(!segment)
      return

    let originalDistance = datumLink.sourceOffsetDistance
    var adjustDistance = Math.abs(newLength) - datumLink.sourceOffsetDistance
    datumLink.sourceOffsetDistance = Math.abs(newLength)
    datumLink.isFixedDatumLink = isUserFixed

    // Solver for moving points around for fixed/non-fixed datum links
    if(!this._runDatumLinkSolver(shape, segment, datumLinkId, -adjustDistance)) {
      // Revert back
      datumLink.sourceOffsetDistance = originalDistance
    }

    // Move points based on datums
    this._updateEditingPointsFromDatums()

    // Recalc dimensions
    this.editor.onToolEditingUpdated(this.selectedShape, this.editingPoints)

    this.editor.render()
  }

  cancelDimensionEdit(userCancelled) {
    let segment = this.selectedShape.getSegmentForIndex(this.selectedSegmentIndex)
    if(!segment)
      return

    // Restore from previous value
    if(userCancelled) {
      var curOffset = 0
      let segDir = segment.getDirection()

      this.datumLinks.forEach((datumLink, index) => {
        if(index < this.editingDatumStoredDistances.length) {
          datumLink.sourceOffsetDistance = this.editingDatumStoredDistances[index]
          datumLink.sourcePosPoint = segment.startPosition.clone().addScaledVector(segDir, curOffset)
          curOffset += datumLink.sourceOffsetDistance
        }
      })
    }

    // Move points based on datums
    this._updateEditingPointsFromDatums()

    // Recalc dimensions
    this.editor.onToolEditingUpdated(this.selectedShape, this.editingPoints)

    if(!userCancelled)
      return

    this.editingDatumLinkId = null
    this.editingDatumStoredDistances = []
    if(userCancelled)
      this.displayToolSettingsPopover()
  }


  /**
   * Update the point positions based on datum link updates (eg, editing dimension)
   */
  _updateEditingPointsFromDatums() {
    this.datumLinks.forEach(datumLink => {
      let pointIndex = datumLink.userData.index

      if(pointIndex < this.editingPoints.length) {
        this.editingPoints[pointIndex].setPosition(datumLink.sourcePosPoint.clone().addScaledVector(datumLink.sourcePosDir, datumLink.sourceOffsetDistance * datumLink.sourceOffsetDirection))
      }
    })
  }


  /**
   * Update the datum link with the solver to adjust all other datums
   */
  _runDatumLinkSolver(shape, segment, editingDatumLinkId, moveDistance) {
    var solutionFound = false
    var moveRequired = moveDistance
    let minMoveLengthStop = 0.5
    var maxAttempts = 2   // Should only need 2 loops
    var loopCounter = 0

    var datumIndex = this.datumLinks.findIndex(datum => datum.id == editingDatumLinkId)
    if(datumIndex == -1)
      return

    var linkDistances = this.datumLinks.map(datumLink => datumLink.sourceOffsetDistance)

    while(!solutionFound && loopCounter++ <= maxAttempts) {

      for(var i=1; i<this.datumLinks.length; i++) {
        if(Math.abs(moveRequired) < minMoveLengthStop) {
          solutionFound = true
          break
        }

        var moveAmount = moveRequired
        if(loopCounter == 1)
          moveAmount = moveRequired / 2.0

        var prevDatumIndex = datumIndex - i
        var nextDatumIndex = datumIndex + i
        if(prevDatumIndex >= 0) {
          if(!this.datumLinks[prevDatumIndex].isFixedDatumLink) {
            if(linkDistances[prevDatumIndex] + moveAmount < 0)
              moveAmount = -linkDistances[prevDatumIndex]

            linkDistances[prevDatumIndex] += moveAmount
            moveRequired -= moveAmount
          }
        }

        if(moveRequired < moveAmount) {
          moveAmount = moveRequired
          if(loopCounter == 1)
            moveAmount = moveRequired / 2.0
        }

        if(nextDatumIndex < this.datumLinks.length) {
          if(!this.datumLinks[nextDatumIndex].isFixedDatumLink) {
            if(linkDistances[nextDatumIndex] + moveAmount < 0)
              moveAmount = -linkDistances[nextDatumIndex]

            linkDistances[nextDatumIndex] += moveAmount
            moveRequired -= moveAmount
          }
        }
      }
    }

    if(!solutionFound)
      return false

    // If total does not match the segment length, solutions is invalid
    let totalDistance = linkDistances.reduce((curValue, dist) => curValue+dist, 0)
    if(Math.abs(totalDistance - segment.getLength()) > 1.0)
      return false

    // Move datums around if solution found
    var curOffset = 0
    let segDir = segment.getDirection()
    this.datumLinks.forEach((datumLink, index) => {
      datumLink.sourceOffsetDistance = linkDistances[index]
      datumLink.sourcePosPoint = segment.startPosition.clone().addScaledVector(segDir, curOffset)
      curOffset += datumLink.sourceOffsetDistance
    })


    return true
  }


}
